// altitudeTerminatorSynthesis — infer a synthetic endpoint for
// altitude-terminator legs (VA, CA, FA).
//
// These legs have no published terminator fix; they're defined as
// "fly this heading/course until altitude X." Without aircraft
// performance data we can't render an exact endpoint, but a
// "best-guess" line is far more useful to a pilot than nothing —
// it shows the rough direction and length of the climb so the
// procedure visually connects to the next fix.
//
// We use two conservative defaults:
//   - climbRateFpm  : 250 ft/min (low-end Part 25 transport / typical
//                      single-engine piston above 5000 ft DA)
//   - groundSpeedKt : 120 kt (standard 'mid-procedure' speed)
//
// At those defaults, 1000 ft of climb takes 4 minutes = 8 nm.
//
// Future work: feed actual aircraft performance from aircraftStore
// when an aircraft is selected.
//
// The synthesis logic intentionally lives OUTSIDE procedureExpansion
// so it can be invoked on demand by the map renderer (which knows
// the prior leg's terminator and can chain projections), without
// rewriting the route on every aircraft swap.

import { destinationPoint } from './geo';
import type { ProcedureLeg } from './cifp/cifp';

const DEFAULT_CLIMB_FPM = 250;
const DEFAULT_GROUND_SPEED_KT = 120;

export interface SynthesisInputs {
  /** Starting point of the leg (terminator of the prior leg). */
  startLat: number;
  startLon: number;
  /** Starting altitude (ft MSL). Used to compute climb distance. */
  startAltFt: number;
  /** Heading or course to fly, degrees magnetic. */
  courseDeg: number;
  /** Target altitude (ft MSL). */
  targetAltFt: number;
  /** Override climb performance for testing or aircraft profiles. */
  climbRateFpm?: number;
  groundSpeedKt?: number;
}

export interface SynthesisResult {
  /** Synthetic endpoint. */
  endLat: number;
  endLon: number;
  /** Distance flown along the heading (nm). */
  distanceNm: number;
  /** Minutes spent climbing. */
  minutes: number;
  /** Whether the result is synthetic (always true; here for callers). */
  synthetic: true;
}

export function synthesizeAltitudeTerminator(p: SynthesisInputs): SynthesisResult {
  const climbFpm = Math.max(50, p.climbRateFpm ?? DEFAULT_CLIMB_FPM);
  const gsKt = Math.max(40, p.groundSpeedKt ?? DEFAULT_GROUND_SPEED_KT);
  const climbFt = Math.max(0, p.targetAltFt - p.startAltFt);
  // Minutes to climb that much.
  const minutes = climbFt / climbFpm;
  // Distance at ground speed.
  const distanceNm = (gsKt * minutes) / 60;
  const dest = destinationPoint(p.startLat, p.startLon, p.courseDeg, distanceNm);
  return {
    endLat: dest.lat,
    endLon: dest.lon,
    distanceNm,
    minutes,
    synthetic: true,
  };
}

// ── Helpers for parser integration ───────────────────────────────────────

/** Try to read a target altitude (ft MSL) from a CIFP altitude string.
 *  Handles both plain '00520' and 'FL250' formats. Returns null on
 *  parse failure or empty input. */
export function parseTargetAltitudeFt(raw: string | null): number | null {
  if (!raw) return null;
  const trimmed = raw.replace(/^[+\-*@]/, '').trim();
  if (!trimmed) return null;
  if (/^FL\d+$/i.test(trimmed)) {
    return Number(trimmed.substring(2)) * 100;
  }
  if (/^\d+$/.test(trimmed)) {
    return Number(trimmed);
  }
  return null;
}

/** Returns true when a leg type has no published terminator fix and
 *  must either be synthesized (when we have a cursor + magCourse + target
 *  altitude) or dropped from the visible plan list.
 *
 *  Includes:
 *   - VA / CA / FA: heading-to-altitude / course-to-altitude / fix-to-altitude
 *   - VI / CI:      heading- / course-to-intercept (terminate when the
 *                   next leg's course is intercepted)
 *   - CR:           course-to-radial (terminate when a VOR radial is
 *                   crossed)
 *   - VM:           heading-to-manual-termination (vectors)
 *
 *  All of these are flight-director instructions, not navigation
 *  waypoints. They produced visible "VA@3443ft" rows in the plan list
 *  before Bug #1/#2/#5 (2026-05-16); now they are either synthesized as
 *  internal dashed-line geometry or dropped silently. */
export function isAltitudeTerminatorLeg(leg: Pick<ProcedureLeg, 'legType'>): boolean {
  const t = leg.legType;
  return t === 'VA' || t === 'CA' || t === 'FA'
      || t === 'VI' || t === 'CI'
      || t === 'CR' || t === 'VM';
}

/** Subset of isAltitudeTerminatorLeg that can be SYNTHESIZED (we know
 *  how to project a synthetic endpoint when the leg has magCourse + a
 *  target altitude). Other altitude-terminator legs (VI/CI/CR/VM) have
 *  no usable terminator info and are silently dropped. */
export function isSynthesizableAltitudeTerminator(
  leg: Pick<ProcedureLeg, 'legType'>,
): boolean {
  const t = leg.legType;
  return t === 'VA' || t === 'CA' || t === 'FA';
}
